import * as Sentry from '@sentry/react';
import {useEffect, useState} from 'react';
import {isRouteErrorResponse, useRouteError} from 'react-router';
import {attemptChunkReload, isChunkLoadError} from '../lib/chunkReload';

/**
 * Root `errorElement` for the data router.
 *
 * React Router's data router catches render/loader errors in its own error
 * boundary. Without an `errorElement` it renders a bare default page and the
 * error never reaches `window.onerror`, so Sentry's global handler (and the
 * `wrapCreateBrowserRouterV7` tracing integration, which does not capture
 * boundary errors) never sees it — that is why the FacebookSignin crash on
 * /auth/login produced no Sentry issue.
 *
 * This boundary forwards genuine render errors to Sentry and shows a minimal,
 * provider-independent fallback (plain DOM + inline styles, no Intl/MUI theme
 * context) so it can render even when the app shell itself failed.
 *
 * Stale-chunk failures after a deploy (a lazy route whose hashed chunk no
 * longer exists on the CDN) are not crashes — they are expected deploy churn
 * that produced SMARTMIXIN-WEB-39. Those are self-healed with a single guarded
 * hard reload and never reported to Sentry; see `lib/chunkReload`.
 */
const RouteErrorBoundary = () => {
  const error = useRouteError();
  // A stale-chunk error renders a neutral "updating" state while we reload;
  // only fall back to the crash UI if the reload was already exhausted.
  const [recovering, setRecovering] = useState(() => isChunkLoadError(error));

  useEffect(() => {
    if (isChunkLoadError(error)) {
      // Reloading pulls the fresh index.html + new chunk hashes. If a reload
      // was already attempted and the chunk still fails (broken deploy /
      // offline), stop looping and show the fallback — but don't report it,
      // it's deploy churn, not an application bug.
      if (!attemptChunkReload()) {
        setRecovering(false);
      }
      return;
    }

    // Intentional route responses (404s, redirects, thrown Responses) are not
    // application crashes — don't report those as Sentry exceptions.
    if (!isRouteErrorResponse(error)) {
      Sentry.captureException(error);
    }
  }, [error]);

  if (recovering) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          color: '#666',
          fontFamily:
            'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        }}>
        <p style={{margin: 0}}>Updating to the latest version…</p>
      </div>
    );
  }

  return (
    <div
      role="alert"
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
        fontFamily:
          'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      }}>
      <h1 style={{margin: 0, fontSize: 24}}>Something went wrong</h1>
      <p style={{margin: 0, maxWidth: 420, color: '#666'}}>
        An unexpected error occurred. Our team has been notified. Please reload
        the page or try again in a moment.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 20px',
          fontSize: 16,
          borderRadius: 8,
          border: 'none',
          backgroundColor: '#1976d2',
          color: '#fff',
          cursor: 'pointer',
        }}>
        Reload
      </button>
    </div>
  );
};

export default RouteErrorBoundary;
